import { Link } from "react-router-dom";
import { Building2, Gauge, Receipt, AlertTriangle, Wrench, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { PageHeader, StatCard, StatusBadge, Loading } from "@/components/common";
import { useFetch, money, fmtDate, CERT_LABELS } from "@/lib/api";
import { useAuth, useCurrency } from "@/context/AuthContext";

function Alerts({ items }) {
  return (
    <div className="panel" data-testid="compliance-alerts">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold">Compliance alerts</h3>
        <Link to="/app/compliance" className="text-sm text-emerald-700 hover:underline" data-testid="dash-compliance-link">View all</Link>
      </div>
      {items.length === 0 ? <p className="text-sm text-slate-500">All certificates are in date.</p> : (
        <ul className="divide-y divide-slate-100">
          {items.map((c) => (
            <li key={c.id} className="py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{CERT_LABELS[c.cert_type]}</div>
                <div className="text-xs text-slate-500 truncate">{c.property_name} · {fmtDate(c.expiry_date)}</div>
              </div>
              <StatusBadge status={c.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { session } = useAuth();
  const cur = useCurrency();
  const { data } = useFetch("/biz/dashboard");
  if (!data) return <Loading />;
  const chart = data.cashflow.map((m) => ({ ...m, label: new Date(`${m.month}-01`).toLocaleDateString("en-GB", { month: "short" }) }));
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Overview" title={`Hello, ${session.user.name?.split(" ")[0] || "there"}`}
        subtitle="Here's how your portfolio is doing today." />
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <StatCard label="Properties" value={data.properties} hint={session.business.type === "agent" ? `${data.landlords} landlords` : `${data.spaces} lettable spaces`} icon={Building2} testid="kpi-properties" />
        <StatCard label="Occupancy" value={`${data.occupancy}%`} hint={`${data.occupied} of ${data.spaces} let`} icon={Gauge} testid="kpi-occupancy" />
        <StatCard label="Rent due" value={money(data.rent_due_month, cur)} hint="this month" icon={Receipt} testid="kpi-rent-due" />
        <StatCard label="Collected" value={money(data.rent_collected_month, cur)} hint="this month" icon={Wallet} tone="green" testid="kpi-collected" />
        <StatCard label="Arrears" value={money(data.arrears, cur)} hint="overdue rent" icon={AlertTriangle} tone={data.arrears > 0 ? "red" : "slate"} testid="kpi-arrears" />
        <StatCard label="Open repairs" value={data.open_maintenance} icon={Wrench} tone={data.open_maintenance ? "amber" : "slate"} testid="kpi-maintenance" />
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="panel lg:col-span-2" data-testid="cashflow-chart">
          <h3 className="text-lg font-bold mb-4">Income vs expenses · last 6 months</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={60} />
                <Tooltip formatter={(v) => money(v, cur)} />
                <Legend />
                <Bar dataKey="income" name="Income" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Expenses" fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <Alerts items={data.compliance_alerts} />
        <div className="panel lg:col-span-3" data-testid="recent-maintenance">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Open maintenance</h3>
            <Link to="/app/maintenance" className="text-sm text-emerald-700 hover:underline">Open board</Link>
          </div>
          {data.recent_maintenance.length === 0 ? <p className="text-sm text-slate-500">No open requests.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.recent_maintenance.map((m) => (
                <div key={m.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex justify-between gap-2"><span className="font-semibold text-sm">{m.title}</span><StatusBadge status={m.priority} /></div>
                  <div className="text-xs text-slate-500 mt-1">{m.property_name} · {m.status.replace("_", " ")}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
